import React, { useState, useRef, useEffect } from "react";
import "../../components/skeletonRow.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faSync,
  faHistory
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import { useNavigate, Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Filler } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Filler);

// Helper: Date Formatting to DD-MM-YYYY HH:mm:ss
const formatDisplayDateTime = (dateStr) => {
  if (!dateStr || dateStr === "NULL" || dateStr === "null" || dateStr === "-") return "-";
  const str = String(dateStr).trim();
  if (str === "" || str === "0000-00-00 00:00:00" || str === "0000-00-00") return "-";

  // If already in DD-MM-YYYY HH:mm:ss format
  if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(str)) return str;
  // If already in DD-MM-YYYY format
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;

  // If in YYYY-MM-DD HH:mm:ss format
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(str)) {
    const [datePart, timePart] = str.split(" ");
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year} ${timePart}`;
  }
  // If in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [year, month, day] = str.split("-");
    return `${day}-${month}-${year}`;
  }

  try {
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
  } catch {
    return str;
  }
};

const defaultFormInputs = {
  email: "",
  email_from: "",
  subject: "",
  status: "",
  from_sent_at: "", to_sent_at: "",
  from_delivered_at: "", to_delivered_at: "",
  from_opened_at: "", to_opened_at: "",
  from_clicked_at: "", to_clicked_at: "",
  from_bounced_at: "", to_bounced_at: "",
  from_complained_at: "", to_complained_at: "",
  from_first_opening_at: "", to_first_opening_at: "",
  from_error_at: "", to_error_at: "",
  from_hard_bounce_at: "", to_hard_bounce_at: "",
  from_soft_bounce_at: "", to_soft_bounce_at: "",
  from_unsubscribed_at: "", to_unsubscribed_at: "",
  from_loaded_by_proxy_at: "", to_loaded_by_proxy_at: ""
};

const defaultFilters = {
  ...defaultFormInputs,
  orderbycol: "sent_at",
  orderby: "desc",
  page: 1,
  limit: 100
};

const EmailStatisticsReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [metrics, setMetrics] = useState({ sends: 0, deliveries: 0, opens: 0, clicks: 0, bounces: 0, errors: 0, unsubscribes: 0, proxies: 0 });
  const [statusCounts, setStatusCounts] = useState([]);
  const [campaignCounts, setCampaignCounts] = useState([]);

  const [isOldLogsMode, setIsOldLogsMode] = useState(() => {
    try {
      const saved = sessionStorage.getItem("email_stats_is_old_logs");
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const [formInputs, setFormInputs] = useState(() => {
    try {
      const saved = sessionStorage.getItem("email_stats_form_inputs");
      return saved ? { ...defaultFormInputs, ...JSON.parse(saved) } : defaultFormInputs;
    } catch {
      return defaultFormInputs;
    }
  });

  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem("email_stats_filters");
      return saved ? { ...defaultFilters, ...JSON.parse(saved) } : defaultFilters;
    } catch {
      return defaultFilters;
    }
  });

  useEffect(() => {
    sessionStorage.setItem("email_stats_filters", JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    sessionStorage.setItem("email_stats_form_inputs", JSON.stringify(formInputs));
  }, [formInputs]);

  useEffect(() => {
    sessionStorage.setItem("email_stats_is_old_logs", JSON.stringify(isOldLogsMode));
  }, [isOldLogsMode]);

  // Refs for double scroll
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Double scroll implementation
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };

    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [data]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const endpoint = isOldLogsMode ? 'email-bravo/statistics' : 'reports/email-statistics';
      const response = await axios.get(`${API_URL}${endpoint}`, {
        params: {
          page: filters.page,
          limit: filters.limit,
          email: filters.email,
          email_from: filters.email_from,
          subject: filters.subject,
          status: filters.status,
          from_sent_at: filters.from_sent_at,
          to_sent_at: filters.to_sent_at,
          from_delivered_at: filters.from_delivered_at,
          to_delivered_at: filters.to_delivered_at,
          from_opened_at: filters.from_opened_at,
          to_opened_at: filters.to_opened_at,
          from_clicked_at: filters.from_clicked_at,
          to_clicked_at: filters.to_clicked_at,
          from_bounced_at: filters.from_bounced_at,
          to_bounced_at: filters.to_bounced_at,
          from_complained_at: filters.from_complained_at,
          to_complained_at: filters.to_complained_at,
          from_first_opening_at: filters.from_first_opening_at,
          to_first_opening_at: filters.to_first_opening_at,
          from_error_at: filters.from_error_at,
          to_error_at: filters.to_error_at,
          from_hard_bounce_at: filters.from_hard_bounce_at,
          to_hard_bounce_at: filters.to_hard_bounce_at,
          from_soft_bounce_at: filters.from_soft_bounce_at,
          to_soft_bounce_at: filters.to_soft_bounce_at,
          from_unsubscribed_at: filters.from_unsubscribed_at,
          to_unsubscribed_at: filters.to_unsubscribed_at,
          from_loaded_by_proxy_at: filters.from_loaded_by_proxy_at,
          to_loaded_by_proxy_at: filters.to_loaded_by_proxy_at,
          orderbycol: filters.orderbycol,
          orderby: filters.orderby,
        },
      });
      if (response.data && response.data.status) {
        setData(response.data.data);
        setTotalRecords(response.data.total);
        if (response.data.metrics) {
          setMetrics(response.data.metrics);
        }
        if (response.data.statusCounts) {
          setStatusCounts(response.data.statusCounts);
        }
        if (response.data.campaignCounts) {
          setCampaignCounts(response.data.campaignCounts);
        }
      }
    } catch (error) {
      console.error("Failed to fetch email statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isOldLogsMode]);

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormInputs(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setFilters(prev => ({
      ...prev,
      ...formInputs,
      page: 1
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const handleSort = (column) => {
    setFilters(prev => ({
      ...prev,
      orderbycol: column,
      orderby: prev.orderbycol === column && prev.orderby === "desc" ? "asc" : "desc",
      page: 1
    }));
  };

  const clearFilters = () => {
    sessionStorage.removeItem("email_stats_filters");
    sessionStorage.removeItem("email_stats_form_inputs");
    sessionStorage.removeItem("email_stats_is_old_logs");
    setFormInputs(defaultFormInputs);
    setFilters(defaultFilters);
  };

  const totalPages = Math.ceil(totalRecords / filters.limit) || 1;
  const startRecord = totalRecords === 0 ? 0 : ((filters.page - 1) * filters.limit) + 1;
  const endRecord = Math.min(filters.page * filters.limit, totalRecords);
  const showingRecords = `${startRecord} to ${endRecord} of ${totalRecords}`;

  const hasAnyFilter = () => {
    return filters.email || filters.email_from || filters.subject || filters.status ||
      filters.from_sent_at || filters.to_sent_at ||
      filters.from_delivered_at || filters.to_delivered_at ||
      filters.from_opened_at || filters.to_opened_at ||
      filters.from_clicked_at || filters.to_clicked_at ||
      filters.from_bounced_at || filters.to_bounced_at ||
      filters.from_complained_at || filters.to_complained_at ||
      filters.from_first_opening_at || filters.to_first_opening_at ||
      filters.from_error_at || filters.to_error_at ||
      filters.from_hard_bounce_at || filters.to_hard_bounce_at ||
      filters.from_soft_bounce_at || filters.to_soft_bounce_at ||
      filters.from_unsubscribed_at || filters.to_unsubscribed_at ||
      filters.from_loaded_by_proxy_at || filters.to_loaded_by_proxy_at ||
      filters.orderbycol !== "sent_at" || filters.orderby !== "desc";
  };

  const getSortIcon = (column) => {
    if (filters.orderbycol !== column) return faSort;
    return filters.orderby === "asc" ? faSortUp : faSortDown;
  };

  const refreshData = () => {
    fetchReportData();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const renderDateRangeInput = (name) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: "135px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
        <span style={{ fontSize: "10px", color: "#666", width: "32px", fontWeight: "600" }}>From:</span>
        <input
          type="date"
          name={`from_${name}`}
          value={formInputs[`from_${name}`] || ""}
          onChange={(e) => {
            handleFormInputChange(e);
            setFilters(prev => ({ ...prev, [`from_${name}`]: e.target.value, page: 1 }));
          }}
          style={{ width: "100%", padding: "2px 4px", fontSize: "11px", borderRadius: "3px", border: "1px solid #ccc" }}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
        <span style={{ fontSize: "10px", color: "#666", width: "32px", fontWeight: "600" }}>To:</span>
        <input
          type="date"
          name={`to_${name}`}
          value={formInputs[`to_${name}`] || ""}
          onChange={(e) => {
            handleFormInputChange(e);
            setFilters(prev => ({ ...prev, [`to_${name}`]: e.target.value, page: 1 }));
          }}
          style={{ width: "100%", padding: "2px 4px", fontSize: "11px", borderRadius: "3px", border: "1px solid #ccc" }}
        />
      </div>
    </div>
  );

  const eventBarData = {
    labels: isOldLogsMode 
      ? ['Sends', 'Deliveries', 'Opens', 'Clicks', 'Bounces', 'Errors', 'Unsubs', 'Proxies']
      : ['Sends', 'Deliveries', 'Opens', 'Clicks', 'Bounces'],
    datasets: [{
      label: 'Email Count',
      data: isOldLogsMode
        ? [metrics.sends, metrics.deliveries, metrics.opens, metrics.clicks, metrics.bounces, metrics.errors || 0, metrics.unsubscribes || 0, metrics.proxies || 0]
        : [metrics.sends, metrics.deliveries, metrics.opens, metrics.clicks, metrics.bounces],
      backgroundColor: isOldLogsMode
        ? ['#8753de', '#00acac', '#348fe2', '#f59c1a', '#ff5b57', '#e74c3c', '#34495e', '#95a5a6']
        : ['#8753de', '#00acac', '#348fe2', '#f59c1a', '#ff5b57'],
      borderWidth: 1,
      borderRadius: 4
    }]
  };

  const statusDoughnutData = {
    labels: statusCounts.map(s => s.status || 'Unknown'),
    datasets: [{
      data: statusCounts.map(s => s.count),
      backgroundColor: ['#348fe2', '#f59c1a', '#ff5b57', '#00acac', '#b6c2c9', '#8753de'],
      borderWidth: 0,
      hoverOffset: 4
    }]
  };

  // const campaignBarData = {
  //   labels: campaignCounts.map(c => c.campaign),
  //   datasets: [{
  //     label: 'Emails',
  //     data: campaignCounts.map(c => c.count),
  //     backgroundColor: '#00acac',
  //     borderWidth: 1,
  //     borderRadius: 4
  //   }]
  // };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <div className="">
          {loading ? (
            <>
              <div className="row mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={`stat-skeleton-${i}`} className="col-lg-2 col-md-4 col-sm-6">
                    <div className="widget widget-stats bg-light inverse-mode" style={{ minHeight: "90px" }}>
                      <div className="widget-stats-left">
                        <div className="skeleton-text" style={{ width: "60px", height: "15px", marginBottom: "5px" }}></div>
                      </div>
                      <div className="widget-stats-right">
                        <div className="skeleton-text" style={{ width: "40px", height: "24px", float: "right" }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="row mb-4">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={`chart-skeleton-${i}`} className="col-lg-6 mb-4">
                    <div className="panel panel-default with-rounded-corner with-shadow h-100">
                      <div className="panel-heading">
                        <h4 className="panel-title"><div className="skeleton-text" style={{ width: "150px" }}></div></h4>
                      </div>
                      <div className="panel-body p-4 d-flex justify-content-center align-items-center">
                        <div className="skeleton-text" style={{ height: "200px", width: "200px", borderRadius: i === 1 ? "50%" : "8px" }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="row mb-4">
                <div className="col-lg-2 col-md-4 col-sm-6">
                  <div className="widget widget-stats bg-gradient-blue-dark inverse-mode">
                    <div className="widget-stats-left">
                      <div className="widget-stats-title">Sends</div>
                    </div>
                    <div className="widget-stats-right">
                      <div className="widget-stats-value">{metrics.sends}</div>
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-2 col-md-4 col-sm-6">
                  <div className="widget widget-stats bg-gradient-green inverse-mode">
                    <div className="widget-stats-left">
                      <div className="widget-stats-title">Deliveries</div>
                    </div>
                    <div className="widget-stats-right">
                      <div className="widget-stats-value">{metrics.deliveries}</div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-2 col-md-4 col-sm-6">
                  <div className="widget widget-stats bg-gradient-blue inverse-mode">
                    <div className="widget-stats-left">
                      <div className="widget-stats-title">Opens</div>
                    </div>
                    <div className="widget-stats-right">
                      <div className="widget-stats-value">{metrics.opens}</div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-2 col-md-4 col-sm-6">
                  <div className="widget widget-stats bg-gradient-orange inverse-mode">
                    <div className="widget-stats-left">
                      <div className="widget-stats-title">Clicks</div>
                    </div>
                    <div className="widget-stats-right">
                      <div className="widget-stats-value">{metrics.clicks}</div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-2 col-md-4 col-sm-6">
                  <div className="widget widget-stats bg-gradient-red inverse-mode">
                    <div className="widget-stats-left">
                      <div className="widget-stats-title">Bounces</div>
                    </div>
                    <div className="widget-stats-right">
                      <div className="widget-stats-value">{metrics.bounces}</div>
                    </div>
                  </div>
                </div>

                {isOldLogsMode && (
                  <>
                    <div className="col-lg-2 col-md-4 col-sm-6">
                      <div className="widget widget-stats bg-gradient-purple inverse-mode">
                        <div className="widget-stats-left">
                          <div className="widget-stats-title">Errors</div>
                        </div>
                        <div className="widget-stats-right">
                          <div className="widget-stats-value">{metrics.errors || 0}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-4 col-sm-6">
                      <div className="widget widget-stats inverse-mode" style={{ background: 'linear-gradient(to right, #667eea, #764ba2)' }}>
                        <div className="widget-stats-left">
                          <div className="widget-stats-title">Unsubscribed</div>
                        </div>
                        <div className="widget-stats-right">
                          <div className="widget-stats-value">{metrics.unsubscribes || 0}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-4 col-sm-6">
                      <div className="widget widget-stats inverse-mode" style={{ background: 'linear-gradient(to right, #f2709c, #ff9472)' }}>
                        <div className="widget-stats-left">
                          <div className="widget-stats-title" style={{color: '#fff'}}>Proxies</div>
                        </div>
                        <div className="widget-stats-right">
                          <div className="widget-stats-value" style={{color: '#fff'}}>{metrics.proxies || 0}</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="row mb-4">
                <div className={`col-lg-${isOldLogsMode ? '8' : '6'} mb-4`}>
                  <div className="panel panel-default with-rounded-corner with-shadow h-100">
                    <div className="panel-heading">
                      <h4 className="panel-title">Engagement Distribution</h4>
                    </div>
                    <div className="panel-body p-4">
                      {metrics.total > 0 ? (
                        <div style={{ height: "260px", position: 'relative' }}>
                          <Bar 
                            data={eventBarData} 
                            options={{ 
                              maintainAspectRatio: false, 
                              plugins: { legend: { display: false } },
                              scales: {
                                y: { beginAtZero: true, ticks: { precision: 0 } }
                              }
                            }} 
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center py-4">No engagement data</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className={`col-lg-${isOldLogsMode ? '4' : '6'} mb-4`}>
                  <div className="panel panel-default with-rounded-corner with-shadow h-100">
                    <div className="panel-heading">
                      <h4 className="panel-title">Status Breakdown</h4>
                    </div>
                    <div className="panel-body p-4">
                      {statusCounts.length > 0 ? (
                        <div style={{ height: "260px", position: 'relative' }}>
                          <Doughnut 
                            data={statusDoughnutData} 
                            options={{ 
                              maintainAspectRatio: false, 
                              cutout: '70%', 
                              plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } } } 
                            }} 
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center py-4">No status data</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="panel-heading d-flex justify-content-between">
            <div className="col-md-4">
              <div style={{ float: "left", position: "relative", marginLeft: "5px" }}>
                <button
                  className={`btn btn-sm mx-1 ${isOldLogsMode ? 'btn-warning' : 'btn-default'}`}
                  onClick={() => {
                    setIsOldLogsMode(!isOldLogsMode);
                    setFilters(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <FontAwesomeIcon icon={isOldLogsMode ? faChevronLeft : faHistory} className="me-1" /> 
                  {isOldLogsMode ? 'Back to Live Logs' : 'Old Logs'}
                </button>
                {hasAnyFilter() && (
                  <button
                    className="btn btn-default btn-xs ml-1"
                    style={{ marginLeft: "6px" }}
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
            <div>
              <span style={{ fontSize: "0.8rem" }} className="me-3">
                {loading ? (
                  <span className="skeleton-text" style={{ width: "140px", display: "inline-block" }}></span>
                ) : (
                  showingRecords
                )}
              </span>

              <button className="btn btn-default btn-sm mx-1" onClick={refreshData}>
                <FontAwesomeIcon icon={faSync} />
              </button>
              <button className="btn btn-default btn-sm mx-1" onClick={() => {}}>
                <FontAwesomeIcon icon={faDownload} /> Export Report
              </button>
              <button
                className={`btn btn-default btn-sm ${filters.page === 1 ? "d-none" : ""}`}
                title="Previous"
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className={`btn btn-default btn-sm ${filters.page === totalPages ? "d-none" : ""}`}
                title="Next"
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page === totalPages}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>

          <div className="panel panel-default with-rounded-corner with-shadow">
            <div className="large-table-container-3">
              <div
                ref={topScrollbarRef}
                onScroll={handleTopScroll}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "16px",
                  marginBottom: "2px",
                  background: "#f8f9fa",
                }}
              >
                <div ref={topScrollbarContentRef} style={{ height: "1px" }}></div>
              </div>

              <div
                ref={tableContainerRef}
                onScroll={syncScroll}
                style={{
                  overflowY: "auto",
                  height: "calc(112vh - 250px)",
                  overflowX: "auto",
                  position: "relative",
                }}
              >
                <table className="table table-condensed table-hover bg-white sticky-table">
                  <thead>
                    <tr style={{ cursor: 'pointer' }}>
                      <th onClick={() => handleSort('email')}>
                        Email <FontAwesomeIcon icon={getSortIcon('email')} />
                      </th>
                      <th style={{ cursor: 'default' }}>Email From</th>
                      <th onClick={() => handleSort('subject')}>
                        Subject <FontAwesomeIcon icon={getSortIcon('subject')} />
                      </th>
                      <th onClick={() => handleSort('sent_at')}>
                        Sent At <FontAwesomeIcon icon={getSortIcon('sent_at')} />
                      </th>
                      <th onClick={() => handleSort('delivered_at')}>
                        Delivered At <FontAwesomeIcon icon={getSortIcon('delivered_at')} />
                      </th>
                      <th onClick={() => handleSort('opened_at')}>
                        Opened At <FontAwesomeIcon icon={getSortIcon('opened_at')} />
                      </th>
                      <th onClick={() => handleSort('clicked_at')}>
                        Clicked At <FontAwesomeIcon icon={getSortIcon('clicked_at')} />
                      </th>
                      <th onClick={() => handleSort('bounced_at')}>
                        Bounced At <FontAwesomeIcon icon={getSortIcon('bounced_at')} />
                      </th>
                      <th onClick={() => handleSort('complained_at')}>
                        Complained At <FontAwesomeIcon icon={getSortIcon('complained_at')} />
                      </th>
                      <th onClick={() => handleSort('status')}>
                        Status <FontAwesomeIcon icon={getSortIcon('status')} />
                      </th>
                      <th onClick={() => handleSort('campaign')}>
                        Campaign <FontAwesomeIcon icon={getSortIcon('campaign')} />
                      </th>
                      {isOldLogsMode && (
                        <>
                          <th onClick={() => handleSort('first_opening_at')}>
                            First Opened <FontAwesomeIcon icon={getSortIcon('first_opening_at')} />
                          </th>
                          <th onClick={() => handleSort('link')}>
                            Link <FontAwesomeIcon icon={getSortIcon('link')} />
                          </th>
                          <th onClick={() => handleSort('error_at')}>
                            Error At <FontAwesomeIcon icon={getSortIcon('error_at')} />
                          </th>
                          <th onClick={() => handleSort('hard_bounce_at')}>
                            Hard Bounce <FontAwesomeIcon icon={getSortIcon('hard_bounce_at')} />
                          </th>
                          <th onClick={() => handleSort('soft_bounce_at')}>
                            Soft Bounce <FontAwesomeIcon icon={getSortIcon('soft_bounce_at')} />
                          </th>
                          <th onClick={() => handleSort('unsubscribed_at')}>
                            Unsubscribed <FontAwesomeIcon icon={getSortIcon('unsubscribed_at')} />
                          </th>
                          <th onClick={() => handleSort('loaded_by_proxy_at')}>
                            Loaded by Proxy <FontAwesomeIcon icon={getSortIcon('loaded_by_proxy_at')} />
                          </th>
                        </>
                      )}
                    </tr>

                    <tr>
                      <th>
                        <input
                          type="text"
                          name="email"
                          placeholder="Search email..."
                          value={formInputs.email}
                          onChange={handleFormInputChange}
                          onKeyDown={handleKeyDown}
                          style={{ width: "120px", padding: "5px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          name="email_from"
                          placeholder="Search email from..."
                          value={formInputs.email_from}
                          onChange={handleFormInputChange}
                          onKeyDown={handleKeyDown}
                          style={{ width: "120px", padding: "5px" }}
                        />
                      </th>
                      <th>
                        <div className="d-flex align-items-center gap-1">
                          <input
                            type="text"
                            name="subject"
                            placeholder="Subject..."
                            value={formInputs.subject}
                            onChange={handleFormInputChange}
                            onKeyDown={handleKeyDown}
                            style={{ width: "120px", padding: "5px" }}
                          />
                          <button type="button" className="btn btn-default btn-xs btn-group-xs" onClick={applyFilters}>
                            <FontAwesomeIcon icon={faSearch} />
                          </button>
                        </div>
                      </th>
                      <th>{renderDateRangeInput("sent_at")}</th>
                      <th>{renderDateRangeInput("delivered_at")}</th>
                      <th>{renderDateRangeInput("opened_at")}</th>
                      <th>{renderDateRangeInput("clicked_at")}</th>
                      <th>{renderDateRangeInput("bounced_at")}</th>
                      <th>{renderDateRangeInput("complained_at")}</th>
                      <th>
                        <select
                          name="status"
                          value={formInputs.status}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormInputs(prev => ({ ...prev, status: value }));
                            setFilters(prev => ({ ...prev, status: value, page: 1 }));
                          }}
                          style={{ width: "100px", padding: "5px" }}
                        >
                          <option value="">--select--</option>
                          <option value="OPEN">OPEN</option>
                          <option value="CLICK">CLICK</option>
                        </select>
                      </th>
                      <th><p>&nbsp;</p></th>
                      {isOldLogsMode && (
                        <>
                          <th>{renderDateRangeInput("first_opening_at")}</th>
                          <th><p>&nbsp;</p></th>
                          <th>{renderDateRangeInput("error_at")}</th>
                          <th>{renderDateRangeInput("hard_bounce_at")}</th>
                          <th>{renderDateRangeInput("soft_bounce_at")}</th>
                          <th>{renderDateRangeInput("unsubscribed_at")}</th>
                          <th>{renderDateRangeInput("loaded_by_proxy_at")}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 2 }).map((_, skeletonIndex) => (
                        <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "65%" }}></div></td>
                          {isOldLogsMode && (
                            <>
                              <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                              <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                              <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                              <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                              <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                              <td><div className="skeleton-text" style={{ width: "65%" }}></div></td>
                              <td><div className="skeleton-text" style={{ width: "85%" }}></div></td>
                            </>
                          )}
                        </tr>
                      ))
                    ) : data.length > 0 ? (
                      data.map((row, index) => (
                        <tr 
                          key={index} 
                          onClick={() => {
                            if (row.message_id) {
                              navigate(`/Report/emailStatistics/${row.message_id}?source=${isOldLogsMode ? 'bravo' : 'standard'}`);
                            }
                          }}
                          style={{ cursor: row.message_id ? 'pointer' : 'default' }}
                          title={row.message_id ? "Click to view event details" : ""}
                        >
                          <td>{row.email}</td>
                          <td className={`ctc ${!row.email_from || row.email_from === "NULL" ? "text-muted font-italic" : ""}`}>{row.email_from && row.email_from !== "NULL" ? row.email_from : "-"}</td>
                          <td>
                            {row.message_id ? (
                              <Link
                                to={`/Report/emailStatistics/${row.message_id}?source=${isOldLogsMode ? 'bravo' : 'standard'}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {row.subject}
                              </Link>
                            ) : row.subject}
                          </td>
                          <td className={`ctc ${formatDisplayDateTime(row.sent_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.sent_at)}</td>
                          <td className={`ctc ${formatDisplayDateTime(row.delivered_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.delivered_at)}</td>
                          <td className={`ctc ${formatDisplayDateTime(row.opened_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.opened_at)}</td>
                          <td className={`ctc ${formatDisplayDateTime(row.clicked_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.clicked_at)}</td>
                          <td className={`ctc ${formatDisplayDateTime(row.bounced_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.bounced_at)}</td>
                          <td className={`ctc ${formatDisplayDateTime(row.complained_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.complained_at)}</td>
                          <td>{row.status}</td>
                          <td className={`ctc ${row.campaign === "NULL" ? "text-muted font-italic" : ""}`}>{row.campaign === "NULL" ? "-" : row.campaign}</td>
                          {isOldLogsMode && (
                            <>
                              <td className={`ctc ${formatDisplayDateTime(row.first_opening_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.first_opening_at)}</td>
                              <td className={`ctc ${!row.link || row.link === "NULL" ? "text-muted font-italic" : ""}`}>
                                {row.link && row.link !== "NULL" ? (
                                  <a href={row.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{row.link.length > 30 ? row.link.substring(0, 30) + '...' : row.link}</a>
                                ) : "-"}
                              </td>
                              <td className={`ctc ${formatDisplayDateTime(row.error_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.error_at)}</td>
                              <td className={`ctc ${formatDisplayDateTime(row.hard_bounce_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.hard_bounce_at)}</td>
                              <td className={`ctc ${formatDisplayDateTime(row.soft_bounce_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.soft_bounce_at)}</td>
                              <td className={`ctc ${formatDisplayDateTime(row.unsubscribed_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.unsubscribed_at)}</td>
                              <td className={`ctc ${formatDisplayDateTime(row.loaded_by_proxy_at) === "-" ? "text-muted font-italic" : ""}`}>{formatDisplayDateTime(row.loaded_by_proxy_at)}</td>
                            </>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        {/* Empty Row Placeholder */}
                      </tr>
                    )}
                  </tbody>
                  {/* Optional Footer */}
                  <tfoot>
                    <tr style={{ fontWeight: "bold", background: "#f8f9fa" }}>
                      <td>Total</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      {isOldLogsMode && (
                        <>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailStatisticsReport;
